import { getItineraryCountries } from 'checkout/lib/utils/cruises/itinerary'
import { excludeNullOrUndefined } from 'checkout/utils'
import { CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2, CHECKOUT_ITEM_TYPE_BEDBANK, CHECKOUT_ITEM_TYPE_LE_HOTEL, CHECKOUT_ITEM_TYPE_VILLA, CHECKOUT_ITEM_TYPE_TOUR_V2, CHECKOUT_ITEM_TYPE_TOUR_V1, CHECKOUT_ITEM_TYPE_CRUISE, CHECKOUT_ITEM_TYPE_FLIGHT, CHECKOUT_ITEM_TYPE_EXPERIENCE } from 'constants/checkout'
import { unique } from 'lib/array/arrayUtils'
import { getTourV2IdForCheckoutItem } from 'lib/checkout/checkoutUtils'
import { findByTwoLetterCode, isValidInsuranceCountry } from 'lib/insurance/insuranceCountries'
import getTourV2CountryCodesVisited from 'lib/tours/getTourV2CountryCodesVisited'
import { createSelector } from 'reselect'
import { getFullOffers } from 'selectors/offerSelectors'

interface DestinationCountriesOptions {
  /**
   * When true, a TourV2 item only contributes countries if its departure is
   * guaranteed. Yielding none is how XCover and Booking Protection suppress
   * themselves for a departure that may not run (they gate on
   * destinationCountries.length). PassportCard is offered on non-guaranteed
   * departures, so its variant reads the itinerary regardless.
   */
  requireGuaranteedTourDeparture: boolean
}

function insuranceCountryNameForProperty(property: App.Property.Property | undefined): string | undefined {
  const location = property?.location
  if (!location) return undefined
  if (isValidInsuranceCountry(location.countryName)) return location.countryName
  if (!location.countryCode) return undefined
  return findByTwoLetterCode(location.countryCode)
}

function createDestinationCountriesFromCartSelector({
  requireGuaranteedTourDeparture,
}: DestinationCountriesOptions) {
  return createSelector(
    (state: App.State) => getFullOffers(state),
    (state: App.State) => state.checkout.cart.items,
    (state: App.State) => state.offer.properties,
    (state: App.State) => state.geo.airports,
    (state: App.State) => state.experience.experiences,
    (state: App.State) => state.geo.currentRegionName,
    (offers, items, properties, airports, experiences, currentRegionName): Array<string> => {
      const destinationCountries = unique(items.map(item => {
        if (item.itemType === CHECKOUT_ITEM_TYPE_BEDBANK) {
          const offer = offers[item.offerId] as App.BedbankOffer
          return offer ? [offer.property.address.countryName] : []
        } else if (item.itemType === CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2) {
          const country = insuranceCountryNameForProperty(properties[item.propertyId]?.property)
          return country ? [country] : []
        } else if (item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL) {
          const offer = offers[item.offerId] as App.Offer
          return offer ? (offer.insuranceCountries?.length ? offer.insuranceCountries : [offer.property?.geoData.country]) : []
        } else if (item.itemType === CHECKOUT_ITEM_TYPE_VILLA) {
          const offer = offers[item.offerId] as App.VillaOffer
          return offer ? (offer.insuranceCountries?.length ? offer.insuranceCountries : [offer.property?.geoData.country]) : []
        } else if (item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2) {
          const offer = offers[getTourV2IdForCheckoutItem(item)] as App.Tours.TourV2Offer
          const departure = offer?.departures[item.purchasableOption.fkDepartureId]

          if (!departure) return []
          if (requireGuaranteedTourDeparture && !departure.isGuaranteed) return []

          const countryCodes = getTourV2CountryCodesVisited(offer.variations[departure.fkVariationId].itinerary)
          return countryCodes.map(code => findByTwoLetterCode(code))
        } else if (item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1) {
          const offer = offers[item.offerId] as App.TourOffer
          if (offer) {
            return offer.locations.length > 0 ? offer.locations : [offer.locationHeading]
          }
        } else if (item.itemType === CHECKOUT_ITEM_TYPE_CRUISE) {
          const offer = offers[item.offerId] as App.CruiseOffer
          if (offer) {
            return getItineraryCountries(offer)
          }
        } else if (item.itemType === CHECKOUT_ITEM_TYPE_FLIGHT) {
          const flightItem = item
          const airport = airports.find(airport => airport.code === flightItem.destinationAirportCode)
          return airport?.countryCode ? [findByTwoLetterCode(airport?.countryCode)] : []
        } else if (item.itemType === CHECKOUT_ITEM_TYPE_EXPERIENCE) {
          const experience = experiences[item.experienceId]
          if (!experience) return []
          const redemptionLocation = experience.redemptionLocations.find((location) => location.id === item.redemptionLocationId)
          return redemptionLocation ? [findByTwoLetterCode(redemptionLocation.countryCode)] : []
        }
      }).flat().filter(excludeNullOrUndefined).filter(isValidInsuranceCountry))

      // For international trips, we only need the destination countries, not the origin country
      if (destinationCountries.some(country => country !== currentRegionName)) {
        return destinationCountries.filter(country => country !== currentRegionName)
      }

      return destinationCountries
    },
  )
}

const getInsuranceDestinationCountriesFromCart = createDestinationCountriesFromCartSelector({
  requireGuaranteedTourDeparture: true,
})

/**
 * PassportCard variant: a tour whose departure is not guaranteed still yields its
 * itinerary countries, because PassportCard is offered on those departures.
 */
export const getPassportCardDestinationCountriesFromCart = createDestinationCountriesFromCartSelector({
  requireGuaranteedTourDeparture: false,
})

export default getInsuranceDestinationCountriesFromCart
